/**
 * Login Page
 * 
 * JWT login with forgot password functionality
 * Uses branding settings for logo, app name, and login background image
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/utils/auth';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import { Mail, ArrowLeft, CheckCircle, KeyRound, AlertTriangle } from 'lucide-react';

const DEFAULT_LOGIN_IMAGE = 'https://images.unsplash.com/photo-1760143769777-66e289e78ae9';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Branding state
  const [branding, setBranding] = useState({
    app_name: 'MemberHub',
    logo_url: null,
    login_image_url: null
  });

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Force password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [tempUserData, setTempUserData] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    loadBranding();
    // Show a clear message if the user was logged out by the session controls.
    // Deferred so the Sonner Toaster has mounted/subscribed before we enqueue.
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');
    if (reason) {
      const messages = {
        idle: 'You were logged out due to inactivity. Please log in again.',
        absolute: 'Your session reached its time limit. Please log in again for security.',
        session_expired: 'Your session expired. Please log in again.',
      };
      const msg = messages[reason];
      if (msg) {
        setTimeout(() => toast.info(msg), 400);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadBranding = async () => {
    try {
      const response = await apiClient.get('/settings/branding');
      setBranding(response.data);
    } catch (error) {
      console.error('Failed to load branding, using defaults:', error);
    }
  };

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate((user.role === 'admin' || user.role === 'super_admin') ? '/admin' : '/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const data = response.data;
      
      // Check if password change is required
      if (data.force_password_change) {
        // Store token temporarily and show password change dialog
        setTempToken(data.token);
        if (data.usage_session_id) localStorage.setItem('usage_session_id', data.usage_session_id);
        setTempUserData(data);
        setShowPasswordChange(true);
        setLoading(false);
        return;
      }
      
      // Normal login flow - use auth context
      localStorage.setItem('jwt_token', data.token);
      if (data.usage_session_id) localStorage.setItem('usage_session_id', data.usage_session_id);
      // Update user state in auth context (this is what was missing!)
      const { setUser } = useAuth;
      
      toast.success('Login successful!');
      
      // Navigate based on role
      const redirectPath = (data.user.role === 'admin' || data.user.role === 'super_admin') ? '/admin' : '/dashboard';
      
      // Force page reload to ensure auth state is properly initialized
      window.location.href = redirectPath;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword === password) {
      toast.error('New password must be different from current password');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      // Use temp token to change password
      await apiClient.post('/auth/change-password', {
        current_password: password,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${tempToken}` }
      });
      
      // Now complete the login
      localStorage.setItem('jwt_token', tempToken);
      
      toast.success('Password changed successfully!');
      setShowPasswordChange(false);
      
      // Navigate based on role - use window.location for full reload
      const redirectPath = (tempUserData.user.role === 'admin' || tempUserData.user.role === 'super_admin') ? '/admin' : '/dashboard';
      window.location.href = redirectPath;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
      setChangingPassword(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setForgotLoading(true);
    try {
      await apiClient.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
    } catch (error) {
      // Still show success to prevent email enumeration
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotSent(false);
  };

  const loginImageUrl = branding.login_image_url || DEFAULT_LOGIN_IMAGE;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-8 bg-background">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="mb-8 flex items-center gap-3">
            {branding.logo_url && branding.use_logo_in_header ? (
              <Link to="/" aria-label="Back to home" data-testid="login-logo-link" className="transition-opacity hover:opacity-80">
                <img src={branding.logo_url} alt={branding.app_name || 'Logo'} className="h-10 w-auto" />
              </Link>
            ) : (
              <Link to="/" className="text-2xl font-heading font-bold text-foreground transition-colors hover:text-primary" data-testid="login-logo-link">
                {branding.app_name || 'MemberHub'}
              </Link>
            )}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-heading">Welcome Back</CardTitle>
              <CardDescription>Sign in to your account to continue</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <button
                      type="button"
                      onClick={() => {
                        setForgotEmail(email);
                        setShowForgotPassword(true);
                      }}
                      className="text-sm text-primary hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link to="/apply" className="text-primary hover:underline">
                  Apply for membership
                </Link>
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Right side - Image */}
      <div
        className="hidden lg:block bg-cover bg-center"
        style={{
          backgroundImage: `url('${loginImageUrl}')`,
        }}
      />

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={closeForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {forgotSent ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <Mail className="w-5 h-5" />
              )}
              {forgotSent ? 'Check Your Email' : 'Forgot Password'}
            </DialogTitle>
            <DialogDescription>
              {forgotSent
                ? 'If an account exists with this email, you will receive a password reset link shortly.'
                : 'Enter your email address and we\'ll send you a link to reset your password.'}
            </DialogDescription>
          </DialogHeader>

          {forgotSent ? (
            <div className="py-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  We've sent a password reset link to <strong>{forgotEmail}</strong>. 
                  Please check your inbox and spam folder.
                </p>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                The link will expire in 1 hour. If you don't receive the email, please check your spam folder or try again.
              </p>
              <DialogFooter className="mt-4">
                <Button onClick={closeForgotPassword} className="w-full">
                  Back to Login
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email Address</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="your@email.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <DialogFooter className="flex flex-col sm:flex-row gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={closeForgotPassword}
                  className="w-full sm:w-auto"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  type="submit" 
                  disabled={forgotLoading}
                  className="w-full sm:w-auto"
                  data-testid="send-reset-link-btn"
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Force Password Change Dialog */}
      <Dialog open={showPasswordChange} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-500" />
              Password Change Required
            </DialogTitle>
            <DialogDescription>
              For security reasons, you must change your password before continuing.
              This is required for new accounts.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handlePasswordChange} className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                Your current password is a default password. Please create a new secure password.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                required
                minLength={8}
                data-testid="new-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                required
                data-testid="confirm-password-input"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>
            
            <DialogFooter className="gap-2">
              <Button 
                type="submit" 
                disabled={changingPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                className="w-full"
                data-testid="change-password-submit-btn"
              >
                {changingPassword ? 'Changing Password...' : 'Change Password & Login'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
